import { SidebarTrigger } from "@/components/ui/sidebar";
import { LanguageSwitcher } from "@/components/landing/LanguageSwitcher";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useNotificationCount } from "@/hooks/useNotificationCount";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { LogOut, ChevronRight, Bell, HelpCircle, ShieldCheck } from "lucide-react";


function crumbsFromPath(path: string): string[] {
  const parts = path.replace(/^\/+|\/+$/g, "").split("/").filter(Boolean);
  if (parts[0] === "dashboard") parts.shift();
  return parts.map((p) =>
    p.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
  );
}

/**
 * Full-width dark navy topbar (Smartlead-style): hamburger + brand on the
 * left, breadcrumbs in the middle, utility actions on the right. Brand
 * accent stays blue.
 */
export function DashboardTopbar() {
  const { signOut, primaryRole } = useAuth();
  const navigate = useNavigate();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const crumbs = crumbsFromPath(path);
  const { total: notifCount, claims, requests, alerts } = useNotificationCount();
  const bellHref = primaryRole === "admin" ? "/dashboard/admin/notifications" : "/dashboard/doctor";
  const bellTitle = notifCount > 0
    ? `${notifCount} pending — ${claims} claims, ${requests} requests${alerts ? `, ${alerts} alerts` : ""}`
    : "No new notifications";

  const onSignOut = async () => {
    await signOut();
    navigate({ to: "/" });
  };


  return (
    <header className="sticky top-0 z-50 h-14 flex items-center gap-3 bg-[oklch(0.18_0.05_265)] text-white px-3 shadow-sm">
      <SidebarTrigger className="h-8 w-8 text-white/80 hover:text-white hover:bg-white/10" />
      <Link to="/" className="flex items-baseline font-bold tracking-tight text-white text-[15px]">
        <span className="text-primary">heall</span>
        <span>exa</span>
        <span className="ml-1 inline-block h-1.5 w-1.5 bg-primary translate-y-[-2px]" aria-hidden />
      </Link>
      <div className="h-5 w-px bg-white/15 mx-1 hidden sm:block" />
      <nav className="flex items-center gap-1.5 text-[13px] min-w-0 truncate text-white/70">
        {crumbs.length === 0 ? (
          <span className="text-white font-medium">Dashboard</span>
        ) : (
          crumbs.map((c, i) => (
            <span key={i} className="flex items-center gap-1.5 min-w-0">
              <span
                className={
                  i === crumbs.length - 1
                    ? "text-white font-medium truncate"
                    : "text-white/60 truncate"
                }
              >
                {c}
              </span>
              {i < crumbs.length - 1 && (
                <ChevronRight className="h-3 w-3 text-white/40 shrink-0" />
              )}
            </span>
          ))
        )}
      </nav>
      <div className="ml-auto flex items-center gap-1">
        <Button variant="ghost" size="icon" className="h-8 w-8 text-white/80 hover:text-white hover:bg-white/10">
          <HelpCircle className="h-4 w-4" />
        </Button>
        <Button
          asChild
          variant="ghost"
          size="icon"
          className="relative h-8 w-8 text-white/80 hover:text-white hover:bg-white/10"
          title={bellTitle}
        >
          <Link to={bellHref as any}>
            <Bell className={`h-4 w-4 ${notifCount > 0 ? "text-red-400" : ""}`} />
            {notifCount > 0 && (
              <span
                aria-label={`${notifCount} unread notifications`}
                className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-[16px] text-center ring-2 ring-[oklch(0.18_0.05_265)]"
              >
                {notifCount > 99 ? "99+" : notifCount}
              </span>
            )}
          </Link>
        </Button>
        <div className="text-white/80 hover:text-white">
          <LanguageSwitcher compact />
        </div>
        <ThemeToggle className="h-8 w-8 text-white/80 hover:text-white hover:bg-white/10" />
        <Link to="/auth/mfa-setup" title="Two-factor authentication">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 gap-1.5 text-xs text-white/80 hover:text-white hover:bg-white/10"
          >
            <ShieldCheck className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Security</span>
          </Button>
        </Link>
        <Button
          variant="ghost"
          size="sm"
          onClick={onSignOut}
          className="h-8 gap-1.5 text-xs text-white/80 hover:text-white hover:bg-white/10"
        >
          <LogOut className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Sign out</span>
        </Button>
      </div>
    </header>
  );
}
